class SocketService {
    private static instance: SocketService
    private socket: WebSocket | null = null
    private url = ""
    private reconnectAttempts = 0
    private readonly reconnectDelay = 1000
    private readonly maxReconnectDelay = 30000
    private reconnectTimeout: number | null = null
    private pingInterval: number | null = null
    private onReconnectCallbacks = new Set<() => void>()
    private lastReconnectTime = 0
    private readonly reconnectThrottleTime = 1000

    /**
     * Get the singleton instance of SocketService.
     * @returns The singleton instance of SocketService.
     */
    public static getInstance(): SocketService {
        if (!SocketService.instance) {
            SocketService.instance = new SocketService()
        }
        return SocketService.instance
    }

    /**
     * Connect to the WebSocket server.
     * @param url The server URL.
     * @param errorCallback Optional callback for handling connection errors.
     */
    public connect(url: string, errorCallback?: (error: Event) => void): void {
        if (!this.socket) {
            this.socket = new WebSocket(url)

            this.socket.onopen = () => {
                console.log("socket connected")
                this.reconnectAttempts = 0

                this.triggerReconnectCallbacks()

                this.pingInterval = window.setInterval(() => {
                    if (!this.isConnected()) {
                        console.log("socket trying to connect")
                        this.socket?.close()
                    }
                }, 5000)
            }

            this.socket.onclose = () => {
                console.log("socket closed")
                this.socket = null
                this.clearPingInterval()
                this.handleReconnect(errorCallback)
            }

            this.socket.onerror = (error) => {
                if (errorCallback) {
                    errorCallback(error)
                }
                this.handleReconnect(errorCallback)
            }
        }
    }

    /**
     * Subscribe to a specific event from the server.
     * @param callback The callback function to handle the message.
     */
    public onMessage<T>(callback: (data: T) => void): void {
        if (this.socket) {
            this.socket.onmessage = (event: MessageEvent) => {
                try {
                    if (typeof event.data !== "string") {
                        console.error("WebSocket message is not a string:", event.data)
                        return
                    }

                    const data: T = JSON.parse(event.data) as T
                    callback(data)
                } catch (error) {
                    console.error("Error parsing JSON:", error, event.data)
                }
            }
        }
    }

    /**
     * Set a callback that will be triggered when the socket successfully reconnects.
     * @param callback The function to execute on reconnect.
     */
    public onReconnect(callback: () => void): void {
        this.onReconnectCallbacks.add(callback)
    }

    /**
     * Unsubscribe a callback from the reconnect event.
     * @param callback The function to remove.
     */
    public offReconnect(callback: () => void): void {
        this.onReconnectCallbacks.delete(callback)
    }

    /**
     * Unsubscribe from messages.
     */
    public offMessage(): void {
        if (this.socket) {
            this.socket.onmessage = null
        }
    }

    /**
     * Emit an event to the server.
     * @param data The data to send.
     * @returns true if the message was actually sent, false if the socket is not open.
     */
    public send<T>(data: T): boolean {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(data))
            return true
        }
        return false
    }

    /**
     * Disconnect from the server and clean up the socket instance.
     */
    public disconnect(): void {
        console.log("socket disconnected")
        if (this.socket) {
            this.socket.close()
            this.socket = null
        }

        if (this.reconnectTimeout !== null) {
            clearTimeout(this.reconnectTimeout)
        }

        this.clearPingInterval()
    }

    /**
     * Check if the socket is connected.
     * @returns True if the socket is in the OPEN state, false otherwise.
     */
    public isConnected(): boolean {
        return this.socket?.readyState === WebSocket.OPEN
    }

    /**
     * Handles WebSocket reconnection logic with unlimited attempts.
     * Uses exponential backoff with a maximum delay limit.
     * @param errorCallback Optional callback for handling connection errors.
     */
    private handleReconnect(errorCallback?: (error: Event) => void): void {
        const delay = Math.min(
            this.reconnectDelay * 2 ** this.reconnectAttempts,
            this.maxReconnectDelay
        )

        console.log("socket is trying to reconnect")

        this.reconnectTimeout = window.setTimeout(() => {
            this.reconnectAttempts++
            this.connect(this.url, errorCallback)
        }, delay)
    }

    /**
     * Clears the ping interval to prevent memory leaks.
     * This should be called when the WebSocket connection is closed or disconnected.
     */
    private clearPingInterval(): void {
        if (this.pingInterval !== null) {
            clearInterval(this.pingInterval)
            this.pingInterval = null
        }
    }

    /**
     * Triggers all registered reconnect callbacks, ensuring they are not called more than once per second.
     */
    private triggerReconnectCallbacks(): void {
        const now = Date.now()
        if (now - this.lastReconnectTime >= this.reconnectThrottleTime) {
            this.lastReconnectTime = now
            this.onReconnectCallbacks.forEach((callback) => callback())
        }
    }
}

export { SocketService }
